import { format } from 'date-fns';
import React, { useState, useEffect } from 'react';
import {
    Box, Typography, Tabs, Tab, Button, Card, CardContent, Grid,
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, Checkbox,
    Dialog, DialogTitle, DialogContent, DialogActions, TextField,
    List, ListItem, ListItemText, Divider, MenuItem, Select, InputLabel, FormControl, Chip, Pagination,
    Tooltip, IconButton, Alert, CircularProgress, Stack
} from '@mui/material';
import DescriptionIcon from '@mui/icons-material/Description';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import CancelIcon from '@mui/icons-material/Cancel';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import VisibilityIcon from '@mui/icons-material/Visibility';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import CloseIcon from '@mui/icons-material/Close';
import SyncIcon from '@mui/icons-material/Sync';
import CandidateDocumentDrawer from '../../components/PreOnboarding/CandidateDocumentDrawer';
import { API } from '../../utils/Axios.utils';
import axios from 'axios';
import useAuth from '../../hooks/useAuth';
import logger from '../../utils/logger';

import { OFFER_STATUS, hasOfferLetter, isEditableOffer, getOfferStats } from '../../utils/offers';

// Kept for existing imports; the helpers live in utils/offers.js.
export { OFFER_STATUS, hasOfferLetter, isEditableOffer };

/** Error text from an API failure: the backend's own message when it sent one. */
const apiErrorMessage = (error, fallback) =>
    error?.response?.data?.error || error?.response?.data?.message || fallback;

const OfferManagement = () => {
    const [currentTab, setCurrentTab] = useState(0);
    const [offers, setOffers] = useState([]);
    const [templates, setTemplates] = useState([]);
    const [selectedOffers, setSelectedOffers] = useState([]);
    const [preOnboardingCandidates, setPreOnboardingCandidates] = useState([]);
    const [selectedPreOnboarding, setSelectedPreOnboarding] = useState([]);
    const [preOnboardingPage, setPreOnboardingPage] = useState(1);
    const [preOnboardingTotalPages, setPreOnboardingTotalPages] = useState(1);
    const [offersPage, setOffersPage] = useState(1);

    // Admins only manage offer templates; recruiters work on candidate offers.
    const { user, organisationId, customerId, isAdmin } = useAuth();

    // Modal states
    const [templateModalOpen, setTemplateModalOpen] = useState(false);
    const [selectedTemplateId, setSelectedTemplateId] = useState(null);
    const [newTemplate, setNewTemplate] = useState({ template_name: '', template_content: '' });

    // Generate Offer Modal States
    const [generateModalOpen, setGenerateModalOpen] = useState(false);
    const [selectedOfferForGen, setSelectedOfferForGen] = useState(null);
    const [selectedGenTemplateId, setSelectedGenTemplateId] = useState('');
    const [generatedText, setGeneratedText] = useState('');

    // Preview Modal States
    const [previewModalOpen, setPreviewModalOpen] = useState(false);
    const [previewOffer, setPreviewOffer] = useState(null);
    const previewRef = React.useRef(null);

    useEffect(() => {
        fetchTemplates();
        if (isAdmin) {
            logger.info('[OfferManagement] Admin — showing offer templates only.');
            return;
        }
        fetchOffers();
        fetchPreOnboardingCandidates();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Pre-onboarding Document Upload & View Modal States
    const [uploadModalOpen, setUploadModalOpen] = useState(false);
    const [selectedCandidateForUpload, setSelectedCandidateForUpload] = useState(null);
    const [selectedFilesToUpload, setSelectedFilesToUpload] = useState({});
    const [uploadSubmitting, setUploadSubmitting] = useState(false);
    const [uploadResults, setUploadResults] = useState(null);

    // Document Details View Modal
    const [docsModalOpen, setDocsModalOpen] = useState(false);
    const [selectedCandidateForDocs, setSelectedCandidateForDocs] = useState(null);
    const [activeDocPreview, setActiveDocPreview] = useState(null);

    // Manual Verification State
    const [selectedDocsToVerify, setSelectedDocsToVerify] = useState({});
    const [verifyReasons, setVerifyReasons] = useState({});
    const [verifyingSubmitting, setVerifyingSubmitting] = useState(false);
    const [verifyAlert, setVerifyAlert] = useState(null);

    // New Production Drawer State
    const [drawerOpen, setDrawerOpen] = useState(false);
    const [selectedCandidateForDrawer, setSelectedCandidateForDrawer] = useState(null);
    const [syncingAllEmails, setSyncingAllEmails] = useState(false);

    const handleSyncAllReplies = async () => {
        try {
            setSyncingAllEmails(true);
            const DOCS_API_URL = process.env.REACT_APP_DOCUMENTS_API_URL || "http://127.0.0.1:8024/api/v1";
            await axios.post(`${DOCS_API_URL}/pre-onboardings/sync-replies`);
            await fetchPreOnboardingCandidates(preOnboardingPage);
        } catch (error) {
            console.error("Error syncing emails:", error);
            alert("Failed to sync emails. Please try again.");
        } finally {
            setSyncingAllEmails(false);
        }
    };

    const fetchOffers = async () => {
        try {
            const response = await API.get('/api/offers');
            setOffers(Array.isArray(response.data) ? response.data : []);
            logger.debug(`[OfferManagement] Loaded ${Array.isArray(response.data) ? response.data.length : 0} offer(s).`);
        } catch (error) {
            logger.error(`[OfferManagement] Failed to load offers — ${apiErrorMessage(error, error.message)}`);
        }
    };

    const fetchPreOnboardingCandidates = async (page = 1) => {
        try {
            const DOCS_API_URL = process.env.REACT_APP_DOCUMENTS_API_URL || "http://127.0.0.1:8024/api/v1";

            const org_id = organisationId || user?.org_id || user?.organisation_id || 307;
            const cust_id = customerId || user?.customer_id || user?.cust_id || 394;

            const payload = {
                org_id: org_id,
                customer_id: cust_id,
                max_rows: 10,
                page_no: page
            };
            const response = await axios.get(`${DOCS_API_URL}/pre-onboardings`, { params: payload });
            if (response.data && response.data.data) {
                setPreOnboardingCandidates(response.data.data);
                // Keep selected candidate for drawer updated in real-time
                setSelectedCandidateForDrawer(prev => {
                    if (!prev) return prev;
                    const prevId = prev.pre_onboarding_id || prev.id || prev.candidate_id;
                    const updated = response.data.data.find(c => (c.pre_onboarding_id || c.id || c.candidate_id) === prevId);
                    return updated || prev;
                });
                if (response.data.pagination) {
                    setPreOnboardingTotalPages(response.data.pagination.total_pages);
                } else {
                    setPreOnboardingTotalPages(1);
                }
                setPreOnboardingPage(page);
            } else {
                setPreOnboardingCandidates([]);
            }
        } catch (error) {
            console.error('Error fetching pre-onboarding candidates:', error);
        }
    };

    const fetchTemplates = async () => {
        try {
            const response = await API.get('/api/offers/templates');
            setTemplates(response.data);
        } catch (error) {
            console.error('Error fetching templates:', error);
        }
    };

    const downloadDocs = async (offerId) => {
        try {
            const response = await API.get(`/api/offers/${offerId}/download-docs`, { responseType: 'blob' });
            
            if (response.data?.type === 'application/json' || (response.data?.size !== undefined && response.data.size < 100)) {
                try {
                    const text = await response.data.text();
                    const json = JSON.parse(text);
                    if (json.error || json.message) {
                        alert(json.error || json.message || "No documents found for this offer.");
                        return;
                    }
                } catch {
                    if (response.data.size === 0) {
                        alert("No documents found in the attachment.");
                        return;
                    }
                }
            }

            const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/zip' }));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', 'documents.zip');
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);
        } catch (error) {
            console.error("Download failed", error);
            alert("Failed to download documents. Please verify that the candidate uploaded valid KYC documents.");
        }
    };

    const handleTabChange = (event, newValue) => {
        setCurrentTab(newValue);
        setSelectedOffers([]); // Clear selection on tab change
        setSelectedPreOnboarding([]);
        setOffersPage(1);
    };

    const handleSelectOffer = (id) => {
        setSelectedOffers(prev =>
            prev.includes(id) ? prev.filter(offerId => offerId !== id) : [...prev, id]
        );
    };

    // Offers that can be ticked on the current tab: drafts with a letter on
    // "Drafts & Approvals" (to submit), offers with a letter on "Out for
    // Signature" (to send).
    const isSelectableOffer = (offer) => {
        if (currentTab === 1) return isEditableOffer(offer) && hasOfferLetter(offer);
        if (currentTab === 2) return hasOfferLetter(offer);
        return true;
    };

    const handleSelectAll = (filteredOffers) => {
        const selectableOffers = filteredOffers.filter(isSelectableOffer);

        if (selectableOffers.length > 0 && selectedOffers.length === selectableOffers.length) {
            setSelectedOffers([]);
        } else {
            setSelectedOffers(selectableOffers.map(o => o.offer_id));
        }
    };

    const handleSelectPreOnboarding = (id) => {
        setSelectedPreOnboarding(prev =>
            prev.includes(id) ? prev.filter(pid => pid !== id) : [...prev, id]
        );
    };

    const handleSelectAllPreOnboarding = (filteredCandidates) => {
        // Only select verified candidates
        const verifiedCandidates = filteredCandidates.filter(c => c.status === 'DOCUMENT_VERIFICATION_COMPLETED');
        if (selectedPreOnboarding.length === verifiedCandidates.length && verifiedCandidates.length > 0) {
            setSelectedPreOnboarding([]);
        } else {
            setSelectedPreOnboarding(verifiedCandidates.map(c => c.candidate_id));
        }
    };

    // Recruiter sends drafts to the managers' Approvals inbox.
    const handleBulkSubmit = async () => {
        logger.info(`[OfferManagement] Submitting offer(s) ${selectedOffers.join(', ')} for approval.`);
        try {
            const { data } = await API.post('/api/offers/bulk-submit', { offer_ids: selectedOffers });
            if (data?.skipped?.length) {
                alert(`${data.processed.length} offer(s) submitted for approval. ${data.skipped.length} could not be submitted (no longer drafts).`);
            }
            fetchOffers();
            setSelectedOffers([]);
        } catch (error) {
            logger.error(`[OfferManagement] Submit for approval failed — ${apiErrorMessage(error, error.message)}`);
            alert(apiErrorMessage(error, 'Failed to submit offers for approval'));
        }
    };

    const handleMoveToOfferStage = async (candidate_or_id) => {
        try {
            const DOCS_API_URL = process.env.REACT_APP_DOCUMENTS_API_URL || "http://127.0.0.1:8024/api/v1";
            let candidateRecord = null;
            let profile_id = candidate_or_id;

            if (typeof candidate_or_id === 'object' && candidate_or_id !== null) {
                candidateRecord = candidate_or_id;
                profile_id = candidateRecord.candidate_id;
            } else {
                candidateRecord = preOnboardingCandidates.find(c => c.candidate_id === candidate_or_id);
            }

            const preOnboardingId = candidateRecord?.pre_onboarding_id;

            // 1. Update stage in external pre-onboarding API via the new batch PATCH /stage endpoint
            if (preOnboardingId) {
                try {
                    await axios.patch(`${DOCS_API_URL}/pre-onboardings/stage`, {
                        stage: "OFFER",
                        pre_onboarding_ids: [preOnboardingId]
                    });
                } catch (extErr) {
                    console.error(`[Move to Offer] External stage update for ${preOnboardingId} failed:`, extErr);
                    const respData = extErr.response?.data;
                    const errMsg = (typeof respData?.detail === 'string' && respData.detail)
                        || respData?.detail?.rejected_pre_onboarding_ids?.[0]?.reason
                        || respData?.detail?.detail
                        || respData?.message
                        || extErr.message;
                    alert(`Could not move candidate to offer stage: ${errMsg}`);
                    return;
                }
            }

            // 2. Internal move to offer stage (if candidate_id is a numeric profile_id in our database)
            if (profile_id && !isNaN(profile_id)) {
                try {
                    await API.post(`/api/pre-onboarding/move-to-offer/${profile_id}`);
                } catch (internalErr) {
                    console.warn(`[Move to Offer] Internal profile ${profile_id} update notice:`, internalErr.response?.data?.error || internalErr.message);
                }
            }

            fetchPreOnboardingCandidates(preOnboardingPage);
            // Also refresh offers as they might have moved to Drafts
            fetchOffers();
        } catch (error) {
            console.error('Error moving candidate to offer stage:', error);
            alert('Failed to move candidate to offer stage');
        }
    };

    const handleBulkMoveToOfferStage = async () => {
        try {
            const DOCS_API_URL = process.env.REACT_APP_DOCUMENTS_API_URL || "http://127.0.0.1:8024/api/v1";

            const preOnboardingIds = selectedPreOnboarding
                .map(candId => {
                    const candidateRecord = preOnboardingCandidates.find(c => c.candidate_id === candId);
                    return candidateRecord?.pre_onboarding_id;
                })
                .filter(Boolean);

            // 1. Update stage for selected candidates in external pre-onboarding API in a single request
            if (preOnboardingIds.length > 0) {
                try {
                    await axios.patch(`${DOCS_API_URL}/pre-onboardings/stage`, {
                        stage: "OFFER",
                        pre_onboarding_ids: preOnboardingIds
                    });
                } catch (extErr) {
                    console.error(`[Bulk Move to Offer] External stage update failed:`, extErr);
                    const respData = extErr.response?.data;
                    const errMsg = (typeof respData?.detail === 'string' && respData.detail)
                        || respData?.detail?.rejected_pre_onboarding_ids?.[0]?.reason
                        || respData?.detail?.detail
                        || respData?.message
                        || extErr.message;
                    alert(`Could not move selected candidates to offer stage: ${errMsg}`);
                    return;
                }
            }

            // 2. Internal bulk move for valid numeric IDs
            const validNumericProfileIds = selectedPreOnboarding.filter(id => !isNaN(id));
            if (validNumericProfileIds.length > 0) {
                try {
                    await API.post('/api/pre-onboarding/bulk-move-to-offer', { profile_ids: validNumericProfileIds });
                } catch (internalErr) {
                    console.warn(`[Bulk Move to Offer] Internal profile update notice:`, internalErr.response?.data?.error || internalErr.message);
                }
            }

            fetchPreOnboardingCandidates(preOnboardingPage);
            fetchOffers();
            setSelectedPreOnboarding([]);
        } catch (error) {
            console.error('Error bulk moving candidates to offer stage:', error);
            alert('Failed to move candidates to offer stage');
        }
    };

    const handleOpenUploadModal = (candidate) => {
        setSelectedCandidateForUpload(candidate);
        setSelectedFilesToUpload({});
        setUploadResults(null);
        setUploadModalOpen(true);
    };

    const handleCloseUploadModal = () => {
        setUploadModalOpen(false);
        setSelectedCandidateForUpload(null);
        setSelectedFilesToUpload({});
        setUploadResults(null);
    };

    const handleOpenDocsModal = (candidate) => {
        setSelectedCandidateForDocs(candidate);
        // Default to the first submitted document with doc_path, if any
        const firstWithDoc = (candidate?.required_documents || []).find(d => Boolean(d.doc_path));
        setActiveDocPreview(firstWithDoc || null);
        setSelectedDocsToVerify({});
        setVerifyReasons({});
        setVerifyAlert(null);
        setDocsModalOpen(true);
    };

    const handleCloseDocsModal = () => {
        setDocsModalOpen(false);
        setSelectedCandidateForDocs(null);
        setActiveDocPreview(null);
        setSelectedDocsToVerify({});
        setVerifyReasons({});
        setVerifyAlert(null);
    };

    const handleToggleDocToVerify = (doc) => {
        if (!doc.document_id) return;
        setSelectedDocsToVerify(prev => {
            const next = { ...prev };
            if (next[doc.document_id]) {
                delete next[doc.document_id];
            } else {
                next[doc.document_id] = true;
                // Pre-fill reason if empty
                if (!verifyReasons[doc.document_id]) {
                    setVerifyReasons(r => ({
                        ...r,
                        [doc.document_id]: `${doc.display_name || doc.doc_type} manually verified`
                    }));
                }
            }
            return next;
        });
    };

    const handleVerifyReasonChange = (document_id, reason) => {
        setVerifyReasons(prev => ({
            ...prev,
            [document_id]: reason
        }));
    };

    const handleManualVerifyDocuments = async () => {
        if (!selectedCandidateForDocs) return;
        const preOnboardingId = selectedCandidateForDocs.pre_onboarding_id || selectedCandidateForDocs.candidate_id;
        const DOCS_API_URL = process.env.REACT_APP_DOCUMENTS_API_URL || "http://127.0.0.1:8024/api/v1";

        const docsToVerifyList = Object.keys(selectedDocsToVerify)
            .filter(docId => selectedDocsToVerify[docId])
            .map(docId => {
                const numId = parseInt(docId, 10);
                const docObj = (selectedCandidateForDocs.required_documents || []).find(d => d.document_id === numId);
                const reason = verifyReasons[docId] || `${docObj?.display_name || docObj?.doc_type || 'Document'} manually verified`;
                return {
                    document_id: numId,
                    is_verified: true,
                    verification_reason: reason
                };
            });

        if (docsToVerifyList.length === 0) {
            alert('Please select at least one document to verify.');
            return;
        }

        const candidateDocs = selectedCandidateForDocs.required_documents || [];
        const allDocsSubmitted = candidateDocs.length > 0 && candidateDocs.every(d => d.status === 'SUBMITTED' || d.is_verified);
        const followups = selectedCandidateForDocs.followup_count !== undefined ? selectedCandidateForDocs.followup_count : 0;
        const maxFollowups = selectedCandidateForDocs.max_followup_count !== undefined ? selectedCandidateForDocs.max_followup_count : 3;

        if (followups < maxFollowups) {
            alert(`Manual verification requires candidate to reach max follow-ups (${followups}/${maxFollowups}).`);
            return;
        }
        if (!allDocsSubmitted) {
            alert('Manual verification is only allowed once all required documents are submitted.');
            return;
        }

        const currentUserId = user?.id || user?.user_id || user?.userId || 123;
        const payload = {
            documents: docsToVerifyList,
            verified_by: currentUserId
        };

        try {
            setVerifyingSubmitting(true);
            setVerifyAlert(null);
            const response = await axios.patch(
                `${DOCS_API_URL}/pre-onboardings/${preOnboardingId}/documents/verification`,
                payload
            );

            if (response.data) {
                setVerifyAlert({
                    type: response.data.failed && response.data.failed.length > 0 ? 'warning' : 'success',
                    message: response.data.failed && response.data.failed.length > 0 
                        ? `Verified ${response.data.successful?.length || 0} document(s). ${response.data.failed.length} failed.`
                        : `Successfully manually verified ${response.data.successful?.length || docsToVerifyList.length} document(s)!`
                });

                // Clear selection
                setSelectedDocsToVerify({});

                // Refresh candidate list to receive updated verified flags and statuses
                await fetchPreOnboardingCandidates(preOnboardingPage);

                // Update the open modal candidate details from refreshed data if available
                const updatedListResponse = await axios.get(`${DOCS_API_URL}/pre-onboardings`, { 
                    params: {
                        org_id: organisationId || user?.org_id || user?.organisation_id || 307,
                        customer_id: customerId || user?.customer_id || user?.cust_id || 394,
                        max_rows: 10,
                        page_no: preOnboardingPage
                    } 
                });
                if (updatedListResponse.data?.data) {
                    const refreshedCandidate = updatedListResponse.data.data.find(
                        c => (c.pre_onboarding_id === selectedCandidateForDocs.pre_onboarding_id || c.candidate_id === selectedCandidateForDocs.candidate_id)
                    );
                    if (refreshedCandidate) {
                        setSelectedCandidateForDocs(refreshedCandidate);
                    }
                }
            }
        } catch (error) {
            console.error('Error in manual verification:', error);
            setVerifyAlert({
                type: 'error',
                message: error.response?.data?.error || error.response?.data?.message || error.message || 'Failed to verify documents'
            });
        } finally {
            setVerifyingSubmitting(false);
        }
    };

    const handleFileSelectForDoc = (doc_type, file) => {
        setSelectedFilesToUpload(prev => ({
            ...prev,
            [doc_type]: file
        }));
    };

    const handleUploadDocuments = async () => {
        if (!selectedCandidateForUpload) return;
        const preOnboardingId = selectedCandidateForUpload.pre_onboarding_id || selectedCandidateForUpload.candidate_id;
        const DOCS_API_URL = process.env.REACT_APP_DOCUMENTS_API_URL || "http://127.0.0.1:8024/api/v1";

        const selectedDocs = Object.entries(selectedFilesToUpload)
            .filter(([_, file]) => Boolean(file))
            .map(([doc_type, file]) => ({ doc_type, file }));

        if (selectedDocs.length === 0) {
            alert('Please select at least one document file to upload.');
            return;
        }

        const formData = new FormData();
        selectedDocs.forEach((doc) => {
            formData.append("doc_types", doc.doc_type);
            formData.append("files", doc.file);
        });

        try {
            setUploadSubmitting(true);
            const response = await axios.post(
                `${DOCS_API_URL}/pre-onboardings/${preOnboardingId}/documents`,
                formData
            );

            if (response.data) {
                setUploadResults(response.data.data || response.data);
                // Refresh table to show newly submitted documents and status
                fetchPreOnboardingCandidates(preOnboardingPage);
            }
        } catch (error) {
            console.error('Error uploading documents:', error);
            alert(error.response?.data?.error || error.response?.data?.message || error.message || 'Failed to upload documents');
        } finally {
            setUploadSubmitting(false);
        }
    };

    const handleBulkSend = async () => {
        logger.info(`[OfferManagement] Sending offer(s) ${selectedOffers.join(', ')}.`);
        try {
            await API.post('/api/offers/bulk-send', { offer_ids: selectedOffers });
            fetchOffers();
            setSelectedOffers([]);
        } catch (error) {
            logger.error(`[OfferManagement] Sending offers failed — ${apiErrorMessage(error, error.message)}`);
            alert(apiErrorMessage(error, 'Failed to send offer emails'));
        }
    };

    const handleOpenGenerateModal = (offer) => {
        setSelectedOfferForGen(offer);
        setSelectedGenTemplateId(offer.template_id || '');
        setGeneratedText(offer.offer_text || '');
        setGenerateModalOpen(true);
    };

    const handleGenTemplateSelect = (e) => {
        const tId = e.target.value;
        setSelectedGenTemplateId(tId);

        if (tId && selectedOfferForGen) {
            const tmpl = templates.find(t => t.template_id === tId);
            if (tmpl) {
                const profile = selectedOfferForGen.profile || {};
                const job = selectedOfferForGen.job || {};

                let html = tmpl.template_content;
                html = html.replace(/{{candidate_name}}/g, profile.candidate_name || '');
                html = html.replace(/{{candidate_email}}/g, profile.email || '');
                html = html.replace(/{{candidate_mobile}}/g, profile.mobile || '');
                html = html.replace(/{{candidate_location}}/g, profile.candidate_current_location || '');
                                const displayJobId = job.job_code || selectedOfferForGen.profile?.job_code || job.job_id || selectedOfferForGen.job_id || '';
                html = html.replace(/{{job_id}}/g, displayJobId);
                html = html.replace(/{{job_title}}/g, job.title || '');
                html = html.replace(/{{job_experience}}/g, job.experience || '');

                let jobLoc = job.location || '';
                if (Array.isArray(jobLoc)) {
                    jobLoc = jobLoc.join(', ');
                } else if (typeof jobLoc === 'string') {
                    let cleaned = jobLoc.trim();
                    if (cleaned.startsWith('[') && cleaned.endsWith(']')) {
                        try { cleaned = JSON.parse(cleaned).join(', '); } catch (e) { }
                    }
                    cleaned = cleaned.replace(/^["']+|["']+$/g, '');
                    jobLoc = cleaned;
                }
                html = html.replace(/{{job_location}}/g, jobLoc || '');

                html = html.replace(/{{job_budget}}/g, `${job.currency || ''} ${job.budget || ''} ${job.budget_uom || ''}`.trim());
                html = html.replace(/{{compensation}}/g, selectedOfferForGen.compensation || '');
                html = html.replace(/{{current_date}}/g, format(new Date(), "dd-MM-yyyy"));

                setGeneratedText(html);
            }
        } else {
            setGeneratedText('');
        }
    };

    const handleSaveGeneratedOffer = async () => {
        try {
            await API.put(`/api/offers/draft/${selectedOfferForGen.offer_id}`, {
                offer_text: generatedText,
                template_id: selectedGenTemplateId
            });
            setGenerateModalOpen(false);
            fetchOffers();
        } catch (error) {
            logger.error(`[OfferManagement] Saving generated letter for offer ${selectedOfferForGen.offer_id} failed — ${apiErrorMessage(error, error.message)}`);
            alert(apiErrorMessage(error, 'Failed to save generated offer'));
        }
    };

    const handleOpenPreview = (offer) => {
        setPreviewOffer(offer);
        setPreviewModalOpen(true);
    };

    const handleClosePreview = () => {
        setPreviewModalOpen(false);
        setPreviewOffer(null);
    };

    const handleSavePreview = async () => {
        if (!previewOffer || !previewRef.current) return;
        const newText = previewRef.current.innerHTML;
        try {
            await API.put(`/api/offers/draft/${previewOffer.offer_id}`, {
                offer_text: newText
            });
            handleClosePreview();
            fetchOffers();
        } catch (error) {
            logger.error(`[OfferManagement] Saving edited letter for offer ${previewOffer.offer_id} failed — ${apiErrorMessage(error, error.message)}`);
            alert(apiErrorMessage(error, 'Failed to save offer'));
        }
    };

    const handleSaveTemplate = async () => {
        try {
            if (selectedTemplateId) {
                await API.put(`/api/offers/templates/${selectedTemplateId}`, newTemplate);
            } else {
                await API.post('/api/offers/templates', newTemplate);
            }
            setNewTemplate({ template_name: '', template_content: '' });
            setSelectedTemplateId(null);
            fetchTemplates();
        } catch (error) {
            console.error('Error saving template:', error);
        }
    };

    const handleDeleteTemplate = async () => {
        if (!selectedTemplateId) return;
        if (!window.confirm('Are you sure you want to delete this template?')) return;

        try {
            await API.delete(`/api/offers/templates/${selectedTemplateId}`);
            setNewTemplate({ template_name: '', template_content: '' });
            setSelectedTemplateId(null);
            fetchTemplates();
        } catch (error) {
            console.error('Error deleting template:', error);
            alert('Failed to delete template');
        }
    };

    // Filter logic
    const drafts = offers.filter(o => 
        (o.status === 'Draft' || o.status === 'Pending_Approval') &&
        (!o.profile?.detailed_status || o.profile?.detailed_status === 'Onboarding_In_Process')
    );
    const outForSignature = offers.filter(o => 
        o.status === 'Out_for_Signature' &&
        (!o.profile?.detailed_status || o.profile?.detailed_status === 'Onboarding_In_Process' || o.profile?.detailed_status === 'Onboarded')
    );
    const accepted = offers.filter(o => o.status === 'Accepted');
    const declined = offers.filter(o => o.status === 'Declined');

    // KPI logic (shared with the manager's Offers page)
    const { sent: offersSentCount, acceptanceRate, avgTimeToAccept: avgTimeStr } = getOfferStats(offers);

    const renderTable = (data, showCheckboxes, actionButton) => {
        const limit = 10;
        const totalPages = Math.max(1, Math.ceil(data.length / limit));
        const effectivePage = Math.min(offersPage, totalPages);
        const paginatedData = data.slice((effectivePage - 1) * limit, effectivePage * limit);

        return (
        <Box>
        <TableContainer component={Paper} sx={{ mt: 2, boxShadow: 3 }}>
            {actionButton && (
                <Box sx={{ p: 2, display: 'flex', justifyContent: 'flex-end', borderBottom: '1px solid #eee' }}>
                    {actionButton}
                </Box>
            )}
            <Table>
                <TableHead sx={{ backgroundColor: '#f5f5f5' }}>
                    <TableRow>
                        {showCheckboxes && (
                            <TableCell padding="checkbox">
                                {(() => {
                                    const selectableCount = paginatedData.filter(isSelectableOffer).length;
                                    return (
                                        <Checkbox 
                                            indeterminate={selectedOffers.length > 0 && selectedOffers.length < selectableCount}
                                            checked={selectableCount > 0 && selectedOffers.length === selectableCount}
                                            onChange={() => handleSelectAll(paginatedData)}
                                            disabled={selectableCount === 0}
                                        />
                                    );
                                })()}
                            </TableCell>
                        )}
                        <TableCell><strong>Candidate</strong></TableCell>
                        <TableCell><strong>Job Code</strong></TableCell>
                        <TableCell><strong>Role</strong></TableCell>
                        <TableCell><strong>Status</strong></TableCell>
                        <TableCell><strong>Actions</strong></TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {paginatedData.length === 0 ? (
                        <TableRow>
                            <TableCell colSpan={showCheckboxes ? 6 : 5} align="center">No records found.</TableCell>
                        </TableRow>
                    ) : (
                        paginatedData.map(row => {
                            const editable = isEditableOffer(row);
                            const pending = row.status === OFFER_STATUS.PENDING_APPROVAL;
                            const rejected = editable && Boolean(row.rejection_reason);
                            return (
                            <TableRow key={row.offer_id} hover>
                                {showCheckboxes && (
                                    <TableCell padding="checkbox">
                                        <Checkbox
                                            checked={selectedOffers.includes(row.offer_id)}
                                            onChange={() => handleSelectOffer(row.offer_id)}
                                            disabled={!isSelectableOffer(row)}
                                        />
                                    </TableCell>
                                )}
                                <TableCell>{row.profile?.candidate_name || 'Unknown'}</TableCell>
                                <TableCell>{row.job?.job_code || row.profile?.job_code || row.job_id || 'N/A'}</TableCell>
                                <TableCell>{row.job?.title || 'N/A'}</TableCell>
                                <TableCell>
                                    <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
                                        <Typography variant="body2">{row.status.replace(/_/g, ' ')}</Typography>
                                        {pending && (
                                            <Chip label="Waiting for manager approval" size="small" color="warning" sx={{ height: 20, fontSize: '0.7rem' }} />
                                        )}
                                        {rejected && (
                                            <Chip label={`Rejected${row.reviewed_by_name ? ` by ${row.reviewed_by_name}` : ''}`} size="small" color="error" sx={{ height: 20, fontSize: '0.7rem' }} />
                                        )}
                                        {row.status === OFFER_STATUS.OUT_FOR_SIGNATURE && row.reviewed_by_name && (
                                            <Chip label={`Approved by ${row.reviewed_by_name}`} size="small" variant="outlined" color="success" sx={{ height: 20, fontSize: '0.7rem' }} />
                                        )}
                                        {row.status === 'Out_for_Signature' && row.sent_date && (
                                            <Chip label="Email Sent" size="small" color="success" sx={{ height: 20, fontSize: '0.7rem' }} />
                                        )}
                                    </Box>
                                    {rejected && (
                                        <Typography variant="caption" color="error" display="block" sx={{ mt: 0.5, whiteSpace: 'pre-wrap' }}>
                                            Reason: {row.rejection_reason}
                                        </Typography>
                                    )}
                                </TableCell>
                                <TableCell>
                                    {row.status === 'Accepted' && row.candidate_message_id === 'SENT_VIA_EMAIL' && (
                                        <Typography variant="body2" color="textSecondary">Docs pending</Typography>
                                    )}
                                    {row.status === 'Accepted' && row.candidate_message_id && row.candidate_message_id.startsWith('nylas://') && (
                                        <Button size="small" variant="outlined" color="primary" onClick={() => downloadDocs(row.offer_id)}>Download Docs</Button>
                                    )}
                                    {row.status === 'Accepted' && row.candidate_message_id && row.candidate_message_id !== 'SENT_VIA_EMAIL' && !row.candidate_message_id.startsWith('nylas://') && (
                                        <Button size="small" variant="outlined" color="primary" onClick={() => window.open(row.candidate_message_id, '_blank')}>Download Docs</Button>
                                    )}
                                    {editable && (
                                        <Box sx={{ display: 'flex', gap: 1 }}>
                                            <Button size="small" variant="contained" color="primary" onClick={() => handleOpenGenerateModal(row)}>Generate Offer Letter</Button>
                                            {row.offer_text && (
                                                <Button size="small" variant="outlined" color="secondary" onClick={() => handleOpenPreview(row)}>Preview & Edit</Button>
                                            )}
                                        </Box>
                                    )}
                                    {pending && row.offer_text && (
                                        <Button size="small" variant="outlined" color="secondary" onClick={() => handleOpenPreview(row)}>Preview Offer</Button>
                                    )}
                                    {row.status === 'Out_for_Signature' && row.offer_text && (
                                        <Button size="small" variant="outlined" color="secondary" sx={{ mt: 1 }} onClick={() => handleOpenPreview(row)}>Preview Offer</Button>
                                    )}
                                </TableCell>
                            </TableRow>
                            );
                        })
                    )}
                </TableBody>
            </Table>
        </TableContainer>
        {totalPages > 1 && (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3, mb: 2 }}>
                <Pagination 
                    count={totalPages} 
                    page={offersPage} 
                    onChange={(e, value) => setOffersPage(value)} 
                    color="primary" 
                />
            </Box>
        )}
        </Box>
    )};

    const renderPreOnboardingTable = (data, actionButton) => {
        const verifiedCandidatesCount = data.filter(c => c.status === 'DOCUMENT_VERIFICATION_COMPLETED').length;
        return (
        <Box>
        <TableContainer component={Paper} sx={{ mt: 2, boxShadow: 3 }}>
            <Box sx={{ p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid #eee' }}>
                <Button
                    size="small"
                    variant="outlined"
                    startIcon={syncingAllEmails ? <CircularProgress size={16} /> : <SyncIcon />}
                    onClick={handleSyncAllReplies}
                    disabled={syncingAllEmails}
                    sx={{ textTransform: 'none', fontWeight: 600, borderRadius: 2 }}
                >
                    {syncingAllEmails ? 'Syncing Nylas Emails...' : 'Sync Nylas Replies'}
                </Button>
                {actionButton && (
                    <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                        {actionButton}
                    </Box>
                )}
            </Box>
            <Table>
                <TableHead sx={{ backgroundColor: '#f5f5f5' }}>
                    <TableRow>
                        <TableCell padding="checkbox">
                            <Checkbox 
                                indeterminate={selectedPreOnboarding.length > 0 && selectedPreOnboarding.length < verifiedCandidatesCount}
                                checked={verifiedCandidatesCount > 0 && selectedPreOnboarding.length === verifiedCandidatesCount}
                                onChange={() => handleSelectAllPreOnboarding(data)}
                                disabled={verifiedCandidatesCount === 0}
                            />
                        </TableCell>
                        <TableCell><strong>Candidate</strong></TableCell>
                        <TableCell><strong>Job Code</strong></TableCell>
                        <TableCell><strong>Role</strong></TableCell>
                        <TableCell><strong>Follow-ups</strong></TableCell>
                        <TableCell><strong>Documents</strong></TableCell>
                        <TableCell><strong>Verification Status</strong></TableCell>
                        <TableCell align="center"><strong>Actions</strong></TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {data.length === 0 ? (
                        <TableRow>
                            <TableCell colSpan={8} align="center">No candidates in pre-onboarding.</TableCell>
                        </TableRow>
                    ) : (
                        data.map(row => {
                            const docs = row.required_documents || [];
                            const totalDocs = docs.length;
                            const submittedDocs = docs.filter(d => d.status === 'SUBMITTED' || d.is_verified).length;
                            const unsubmittedDocs = docs.filter(d => d.status === 'NOT_SUBMITTED' || !d.status);
                            const allDocsVerified = totalDocs > 0 ? docs.every(d => d.is_verified || d.status === 'VERIFIED') : (row.status === 'DOCUMENT_VERIFICATION_COMPLETED');
                            const isVerified = (row.status === 'DOCUMENT_VERIFICATION_COMPLETED' || allDocsVerified) && (totalDocs === 0 || allDocsVerified);
                            const followups = row.followup_count !== undefined ? row.followup_count : 0;
                            const maxFollowups = row.max_followup_count !== undefined ? row.max_followup_count : 3;
                            const isEligibleForUpload = followups >= maxFollowups && unsubmittedDocs.length > 0;

                            return (
                                <TableRow key={row.candidate_id || row.pre_onboarding_id} hover>
                                    <TableCell padding="checkbox">
                                        <Checkbox 
                                            checked={selectedPreOnboarding.includes(row.candidate_id)}
                                            onChange={() => handleSelectPreOnboarding(row.candidate_id)}
                                            disabled={!isVerified}
                                        />
                                    </TableCell>
                                    <TableCell>
                                        <Typography variant="body2" fontWeight="600">{row.candidate_name || 'Unknown'}</Typography>
                                        <Typography variant="caption" color="textSecondary">ID: {row.candidate_id || 'N/A'}</Typography>
                                    </TableCell>
                                    <TableCell>{row.job_code || row.job_id || 'N/A'}</TableCell>
                                    <TableCell>{row.role || 'N/A'}</TableCell>
                                    <TableCell>
                                        <Chip 
                                            label={`${followups}/${maxFollowups} Reminders`}
                                            size="small"
                                            variant={followups >= maxFollowups ? "filled" : "outlined"}
                                            color={followups >= maxFollowups ? "warning" : "default"}
                                            sx={{ fontSize: '0.75rem', fontWeight: followups >= maxFollowups ? 'bold' : 'normal' }}
                                        />
                                    </TableCell>
                                    <TableCell>
                                        {totalDocs > 0 ? (
                                            <Tooltip title="Click to view candidate documents in drawer">
                                                <Button 
                                                    size="small" 
                                                    variant="outlined"
                                                    color={submittedDocs === totalDocs ? "success" : (submittedDocs > 0 ? "primary" : "inherit")}
                                                    startIcon={<VisibilityIcon fontSize="small" />}
                                                    onClick={() => {
                                                        setSelectedCandidateForDrawer(row);
                                                        setDrawerOpen(true);
                                                    }}
                                                    sx={{ 
                                                        textTransform: 'none', 
                                                        px: 1.5, 
                                                        py: 0.5,
                                                        borderRadius: 2,
                                                        fontWeight: 600,
                                                        fontSize: '0.8rem',
                                                        whiteSpace: 'nowrap',
                                                        boxShadow: '0 1px 2px rgba(0,0,0,0.05)',
                                                        '&:hover': {
                                                             boxShadow: '0 2px 4px rgba(0,0,0,0.12)'
                                                        }
                                                    }}
                                                >
                                                    {submittedDocs}/{totalDocs} Docs Review
                                                </Button>
                                            </Tooltip>
                                        ) : (
                                            <Typography variant="caption" color="textSecondary">No docs requested</Typography>
                                        )}
                                    </TableCell>
                                    <TableCell>
                                        <Chip 
                                            label={isVerified ? 'Verified' : (row.status ? row.status.replace(/_/g, ' ') : 'Pending Verification')} 
                                            size="small" 
                                            color={isVerified ? 'success' : 'warning'} 
                                            sx={{ fontWeight: 'bold' }} 
                                        />
                                    </TableCell>
                                    <TableCell align="center">
                                        <Stack direction="row" spacing={1} justifyContent="center">
                                            {/* Manual Upload Document button */}
                                            {totalDocs > 0 && !isVerified && (
                                                <Tooltip 
                                                    title={
                                                        followups < maxFollowups 
                                                            ? `Manual upload enabled after ${maxFollowups} follow-ups (Current: ${followups})` 
                                                            : unsubmittedDocs.length === 0 
                                                            ? 'All documents already submitted' 
                                                            : 'Upload missing documents manually'
                                                    }
                                                >
                                                    <span>
                                                        <Button
                                                            size="small"
                                                            variant="outlined"
                                                            color="secondary"
                                                            startIcon={<CloudUploadIcon />}
                                                            disabled={!isEligibleForUpload}
                                                            onClick={() => {
                                                                setSelectedCandidateForDrawer(row);
                                                                setDrawerOpen(true);
                                                            }}
                                                            sx={{ textTransform: 'none', whiteSpace: 'nowrap' }}
                                                        >
                                                            Upload Docs
                                                        </Button>
                                                    </span>
                                                </Tooltip>
                                            )}

                                            <Tooltip title={!isVerified ? "All required documents must be verified before moving to Offer Stage" : "Move to Offer Stage"}>
                                                <span>
                                                    <Button 
                                                        size="small" 
                                                        variant="contained" 
                                                        color="primary" 
                                                        disabled={!isVerified}
                                                        onClick={() => handleMoveToOfferStage(row)}
                                                        sx={{ textTransform: 'none', whiteSpace: 'nowrap' }}
                                                    >
                                                        Move to Offer Stage
                                                    </Button>
                                                </span>
                                            </Tooltip>
                                        </Stack>
                                    </TableCell>
                                </TableRow>
                            );
                        })
                    )}
                </TableBody>
            </Table>
        </TableContainer>
        {preOnboardingTotalPages > 1 && (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3, mb: 2 }}>
                <Pagination 
                    count={preOnboardingTotalPages} 
                    page={preOnboardingPage} 
                    onChange={(e, value) => fetchPreOnboardingCandidates(value)} 
                    color="primary" 
                />
            </Box>
        )}
        </Box>
    )};

    // Derived state for Candidate Documents & Verification Modal
    const candidateDocs = selectedCandidateForDocs?.required_documents || [];
    const totalCandidateDocs = candidateDocs.length;
    const submittedCandidateDocsList = candidateDocs.filter(d => d.status === 'SUBMITTED' || d.is_verified);
    const submittedCandidateDocsCount = submittedCandidateDocsList.length;
    const allCandidateDocsSubmitted = totalCandidateDocs > 0 && candidateDocs.every(d => d.status === 'SUBMITTED' || d.is_verified);
    const candidateFollowups = selectedCandidateForDocs?.followup_count !== undefined ? selectedCandidateForDocs.followup_count : 0;
    const candidateMaxFollowups = selectedCandidateForDocs?.max_followup_count !== undefined ? selectedCandidateForDocs.max_followup_count : 3;
    const isCandidateMaxFollowupsReached = candidateFollowups >= candidateMaxFollowups;
    const isManualVerificationEligible = allCandidateDocsSubmitted && isCandidateMaxFollowupsReached;

    return (
        <Box sx={{ padding: '30px', backgroundColor: '#f9fafb', minHeight: '100vh' }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Box>
                    <Typography variant="h4" fontWeight="bold" color="primary" gutterBottom>
                        {isAdmin ? 'Offer Templates' : 'Offer Management'}
                    </Typography>
                    <Typography variant="subtitle1" color="textSecondary">
                        {isAdmin
                            ? 'Create and maintain the offer letter templates recruiters use.'
                            : 'Generate offer letters, submit them for manager approval, and track e-signatures.'}
                    </Typography>
                </Box>
                {isAdmin && (
                    <Button
                        variant="contained"
                        color="primary"
                        startIcon={<DescriptionIcon />}
                        onClick={() => setTemplateModalOpen(true)}
                        sx={{ borderRadius: 2, textTransform: 'none', px: 3, py: 1 }}
                    >
                        Template Manager
                    </Button>
                )}
            </Box>

            {/* Admin: offer templates only */}
            {isAdmin && (
                <Paper sx={{ borderRadius: 3, p: 3 }}>
                    <Typography variant="h6" fontWeight="bold" mb={1}>Offer Letter Templates ({templates.length})</Typography>
                    {templates.length === 0 ? (
                        <Typography variant="body2" color="textSecondary">No templates yet. Use Template Manager to create one.</Typography>
                    ) : (
                        <List sx={{ p: 0 }}>
                            {templates.map(t => (
                                <ListItem
                                    button
                                    key={t.template_id}
                                    divider
                                    onClick={() => {
                                        setSelectedTemplateId(t.template_id);
                                        setNewTemplate({ template_name: t.template_name, template_content: t.template_content });
                                        setTemplateModalOpen(true);
                                    }}
                                >
                                    <ListItemText primary={t.template_name} secondary="Click to edit" />
                                </ListItem>
                            ))}
                        </List>
                    )}
                </Paper>
            )}

            {!isAdmin && (<>
            {/* KPI Cards */}
            <Grid container spacing={3} sx={{ mb: 4 }}>
                <Grid item xs={12} sm={6} md>
                    <Card sx={{ borderRadius: 3, boxShadow: '0 4px 12px rgba(0,0,0,0.05)' }}>
                        <CardContent>
                            <Box display="flex" alignItems="center" justifyContent="space-between">
                                <Typography color="textSecondary" fontWeight="bold">Offers Sent</Typography>
                                <DescriptionIcon color="primary" />
                            </Box>
                            <Typography variant="h3" sx={{ mt: 2, fontWeight: 'bold' }}>{offersSentCount}</Typography>
                        </CardContent>
                    </Card>
                </Grid>
                <Grid item xs={12} sm={6} md>
                    <Card sx={{ borderRadius: 3, boxShadow: '0 4px 12px rgba(0,0,0,0.05)' }}>
                        <CardContent>
                            <Box display="flex" alignItems="center" justifyContent="space-between">
                                <Typography color="textSecondary" fontWeight="bold">Acceptance Rate</Typography>
                                <CheckCircleIcon color="success" />
                            </Box>
                            <Typography variant="h3" sx={{ mt: 2, fontWeight: 'bold' }}>{acceptanceRate}%</Typography>
                        </CardContent>
                    </Card>
                </Grid>
                <Grid item xs={12} sm={6} md>
                    <Card sx={{ borderRadius: 3, boxShadow: '0 4px 12px rgba(0,0,0,0.05)' }}>
                        <CardContent>
                            <Box display="flex" alignItems="center" justifyContent="space-between">
                                <Typography color="textSecondary" fontWeight="bold">Avg Time to Accept</Typography>
                                <AccessTimeIcon color="warning" />
                            </Box>
                            <Typography variant="h3" sx={{ mt: 2, fontWeight: 'bold' }}>{avgTimeStr}</Typography>
                        </CardContent>
                    </Card>
                </Grid>
                <Grid item xs={12} sm={6} md>
                    <Card sx={{ borderRadius: 3, boxShadow: '0 4px 12px rgba(0,0,0,0.05)' }}>
                        <CardContent>
                            <Box display="flex" alignItems="center" justifyContent="space-between">
                                <Typography color="textSecondary" fontWeight="bold">Accepted</Typography>
                                <CheckCircleIcon color="success" />
                            </Box>
                            <Typography variant="h3" sx={{ mt: 2, fontWeight: 'bold' }}>{accepted.length}</Typography>
                        </CardContent>
                    </Card>
                </Grid>
                <Grid item xs={12} sm={6} md>
                    <Card sx={{ borderRadius: 3, boxShadow: '0 4px 12px rgba(0,0,0,0.05)' }}>
                        <CardContent>
                            <Box display="flex" alignItems="center" justifyContent="space-between">
                                <Typography color="textSecondary" fontWeight="bold">Declined</Typography>
                                <CancelIcon color="error" />
                            </Box>
                            <Typography variant="h3" sx={{ mt: 2, fontWeight: 'bold' }}>{declined.length}</Typography>
                        </CardContent>
                    </Card>
                </Grid>
            </Grid>

            {/* Tabs Interface */}
            <Paper sx={{ borderRadius: 3, overflow: 'hidden' }}>
                <Box sx={{ borderBottom: 1, borderColor: 'divider', bgcolor: 'white' }}>
                    <Tabs value={currentTab} onChange={handleTabChange} indicatorColor="primary" textColor="primary" variant="scrollable" scrollButtons="auto" sx={{ px: 2 }}>
                        <Tab label={`Pre-Onboarding (${preOnboardingCandidates.length})`} sx={{ fontWeight: 'bold', textTransform: 'none' }} />
                        <Tab label={`Drafts & Approvals (${drafts.length})`} sx={{ fontWeight: 'bold', textTransform: 'none' }} />
                        <Tab label={`Out for Signature (${outForSignature.length})`} sx={{ fontWeight: 'bold', textTransform: 'none' }} />
                        <Tab label={`Accepted (${accepted.length})`} sx={{ fontWeight: 'bold', textTransform: 'none' }} />
                    </Tabs>
                </Box>

                <Box sx={{ p: 0, bgcolor: 'white' }}>
                    {currentTab === 0 && renderPreOnboardingTable(
                        preOnboardingCandidates,
                        <Button variant="contained" color="primary" onClick={handleBulkMoveToOfferStage} disabled={selectedPreOnboarding.length === 0}>
                            Move Selected to Offer Stage ({selectedPreOnboarding.length})
                        </Button>
                    )}
                    {currentTab === 1 && renderTable(
                        drafts,
                        true,
                        <Button variant="contained" color="success" onClick={handleBulkSubmit} disabled={selectedOffers.length === 0}>
                            Submit for Approval ({selectedOffers.length})
                        </Button>
                    )}
                    {currentTab === 2 && renderTable(
                        outForSignature, 
                        true, 
                        <Button 
                            variant="contained" 
                            color="primary" 
                            onClick={handleBulkSend} 
                            disabled={selectedOffers.length === 0}
                        >
                            {selectedOffers.some(id => outForSignature.find(o => o.offer_id === id)?.sent_date) 
                                ? `Send / Resend Offer Emails (${selectedOffers.length})` 
                                : `Send Offer Emails (${selectedOffers.length})`}
                        </Button>
                    )}
                    {currentTab === 3 && renderTable(accepted, false, null)}
                </Box>
            </Paper>
            </>)}

            {/* Template Manager Modal */}
            <Dialog
                open={templateModalOpen}
                onClose={(event, reason) => {
                    if (reason !== 'backdropClick') setTemplateModalOpen(false);
                }}
                maxWidth="md"
                fullWidth
            >
                <DialogTitle>Template Manager</DialogTitle>
                <DialogContent sx={{ display: 'flex', gap: 3, p: 0 }}>
                    <Box sx={{ width: '30%', borderRight: 1, borderColor: 'divider', bgcolor: '#f9fafb' }}>
                        <Box sx={{ p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <Typography variant="subtitle1" fontWeight="bold">Templates</Typography>
                            <Button size="small" onClick={() => {
                                setSelectedTemplateId(null);
                                setNewTemplate({ template_name: '', template_content: '' });
                            }}>+ New</Button>
                        </Box>
                        <Divider />
                        <List sx={{ p: 0 }}>
                            {templates.map(t => (
                                <ListItem
                                    button
                                    key={t.template_id}
                                    selected={selectedTemplateId === t.template_id}
                                    onClick={() => {
                                        setSelectedTemplateId(t.template_id);
                                        setNewTemplate({ template_name: t.template_name, template_content: t.template_content });
                                    }}
                                >
                                    <ListItemText primary={t.template_name} />
                                </ListItem>
                            ))}
                            {templates.length === 0 && (
                                <Typography variant="body2" color="textSecondary" sx={{ p: 2 }}>No templates found.</Typography>
                            )}
                        </List>
                    </Box>
                    <Box sx={{ width: '70%', p: 3 }}>
                        <Typography variant="h6" mb={2}>
                            {selectedTemplateId ? 'Edit Template' : 'Create New Template'}
                        </Typography>
                        <Typography variant="body2" color="textSecondary" mb={2}>
                            Available placeholders: <br />
                            {`{{candidate_name}}`}, {`{{candidate_email}}`}, {`{{candidate_mobile}}`}, {`{{candidate_location}}`}<br />
                            {`{{job_id}}`}, {`{{job_title}}`}, {`{{job_location}}`}, {`{{job_experience}}`}, {`{{job_budget}}`}<br />
                            {`{{compensation}}`}, {`{{current_date}}`}
                        </Typography>
                        <TextField
                            fullWidth label="Template Name" variant="outlined" margin="normal"
                            value={newTemplate.template_name}
                            onChange={(e) => setNewTemplate({ ...newTemplate, template_name: e.target.value })}
                        />
                        <TextField
                            fullWidth label="Template HTML Content" variant="outlined" margin="normal" multiline rows={12}
                            value={newTemplate.template_content}
                            onChange={(e) => setNewTemplate({ ...newTemplate, template_content: e.target.value })}
                        />
                    </Box>
                </DialogContent>
                <DialogActions sx={{ p: 2, borderTop: 1, borderColor: 'divider', display: 'flex', justifyContent: 'space-between' }}>
                    <Box>
                        {selectedTemplateId && (
                            <Button color="error" onClick={handleDeleteTemplate}>Delete Template</Button>
                        )}
                    </Box>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                        <Button onClick={() => setTemplateModalOpen(false)}>Close</Button>
                        <Button variant="contained" onClick={handleSaveTemplate} disabled={!newTemplate.template_name || !newTemplate.template_content}>
                            {selectedTemplateId ? 'Update Template' : 'Save New Template'}
                        </Button>
                    </Box>
                </DialogActions>
            </Dialog>

            {/* Generate Offer Modal */}
            <Dialog
                open={generateModalOpen}
                onClose={(event, reason) => {
                    if (reason !== 'backdropClick') setGenerateModalOpen(false);
                }}
                maxWidth="md"
                fullWidth
            >
                <DialogTitle>Generate Offer Letter</DialogTitle>
                <DialogContent>
                    <FormControl fullWidth margin="normal">
                        <InputLabel>Select Template</InputLabel>
                        <Select
                            value={selectedGenTemplateId}
                            onChange={handleGenTemplateSelect}
                            label="Select Template"
                        >
                            <MenuItem value=""><em>None</em></MenuItem>
                            {templates.map(t => (
                                <MenuItem key={t.template_id} value={t.template_id}>{t.template_name}</MenuItem>
                            ))}
                        </Select>
                    </FormControl>

                    <Typography variant="body2" color="textSecondary" sx={{ mt: 2, mb: 1 }}>
                        The content below is automatically filled from the template. You can manually edit it before saving.
                    </Typography>

                    <TextField
                        fullWidth variant="outlined" margin="normal" multiline rows={15}
                        value={generatedText}
                        onChange={(e) => setGeneratedText(e.target.value)}
                        placeholder="Offer letter text goes here..."
                    />
                </DialogContent>
                <DialogActions sx={{ p: 2 }}>
                    <Button onClick={() => setGenerateModalOpen(false)}>Cancel</Button>
                    <Button variant="contained" onClick={handleSaveGeneratedOffer} disabled={!generatedText.trim()}>
                        Save & Generate
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Preview Modal */}
            <Dialog
                open={previewModalOpen}
                onClose={(event, reason) => {
                    if (reason !== 'backdropClick') handleClosePreview();
                }} 
                TransitionProps={{ unmountOnExit: true }}
                maxWidth="md" 
                fullWidth
            >
                <DialogTitle>{isEditableOffer(previewOffer) ? 'Preview & Edit Offer Letter' : 'Preview Offer Letter'}</DialogTitle>
                <DialogContent dividers>
                    <Typography variant="caption" color="textSecondary" display="block" mb={2}>
                        {isEditableOffer(previewOffer)
                            ? 'You can click inside the document below to visually edit the text before saving.'
                            : previewOffer?.status === OFFER_STATUS.PENDING_APPROVAL
                                ? 'This offer is waiting for manager approval and is read-only.'
                                : 'This offer is currently out for signature and is read-only.'}
                    </Typography>
                    <Box
                        ref={previewRef}
                        contentEditable={isEditableOffer(previewOffer)}
                        suppressContentEditableWarning={true}
                        dangerouslySetInnerHTML={{ __html: previewOffer?.offer_text || '' }}
                        sx={{
                            p: 3,
                            bgcolor: '#fff',
                            border: '1px solid #ddd',
                            borderRadius: 1,
                            minHeight: '300px',
                            '&:focus': { outline: '2px solid #1976d2', border: 'none' }
                        }}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleClosePreview}>Close</Button>
                    {isEditableOffer(previewOffer) && (
                        <Button variant="contained" color="primary" onClick={handleSavePreview}>Save Changes</Button>
                    )}
                </DialogActions>
            </Dialog>

            {/* View Candidate Documents Dialog (Split-View In-App Previewer) */}
            <Dialog
                open={docsModalOpen}
                onClose={(event, reason) => {
                    if (reason === 'backdropClick') return;
                    handleCloseDocsModal();
                }}
                maxWidth="lg"
                fullWidth
            >
                <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pb: 1.5 }}>
                    <Box>
                        <Typography variant="h6" fontWeight="bold">Candidate Documents & Verification</Typography>
                        {selectedCandidateForDocs && (
                            <Typography variant="body2" color="textSecondary">
                                {selectedCandidateForDocs.candidate_name} &bull; ID: {selectedCandidateForDocs.candidate_id} &bull; Role: {selectedCandidateForDocs.role || 'N/A'}
                            </Typography>
                        )}
                    </Box>
                    <IconButton size="small" onClick={handleCloseDocsModal}>
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>
                <DialogContent dividers sx={{ p: 0, height: '70vh', minHeight: '520px', display: 'flex', overflow: 'hidden' }}>
                    {selectedCandidateForDocs && (
                        <>
                            {/* Left Pane: Document Checklist (42%) */}
                            <Box sx={{ width: '42%', borderRight: '1px solid #e2e8f0', p: 2, overflowY: 'auto', bgcolor: '#f8fafc', display: 'flex', flexDirection: 'column' }}>
                                <Box display="flex" justifyContent="space-between" alignItems="center" mb={1.5}>
                                    <Typography variant="subtitle2" fontWeight="700" color="textPrimary">
                                        Required Documents ({submittedCandidateDocsCount}/{totalCandidateDocs})
                                    </Typography>
                                    <Chip 
                                        label={`${candidateFollowups}/${candidateMaxFollowups} Reminders`}
                                        size="small" 
                                        variant="outlined" 
                                        color={isCandidateMaxFollowupsReached ? "warning" : "default"} 
                                        sx={{ fontSize: '0.7rem', fontWeight: isCandidateMaxFollowupsReached ? 'bold' : 'normal' }}
                                    />
                                </Box>

                                {/* Informational banners based on manual verification rules */}
                                {isManualVerificationEligible ? (
                                    <Alert severity="info" sx={{ mb: 1.5, py: 0.5, fontSize: '0.75rem' }}>
                                        Max follow-ups reached ({candidateFollowups}/{candidateMaxFollowups}) and all required documents are submitted. You can select checkboxes next to submitted documents to manually verify & approve them.
                                    </Alert>
                                ) : !isCandidateMaxFollowupsReached ? (
                                    <Alert severity="info" sx={{ mb: 1.5, py: 0.5, fontSize: '0.75rem', bgcolor: '#f1f5f9', border: '1px solid #e2e8f0', color: 'text.secondary' }}>
                                        Manual verification is locked until candidate reaches maximum follow-ups (Current: {candidateFollowups}/{candidateMaxFollowups}).
                                    </Alert>
                                ) : !allCandidateDocsSubmitted ? (
                                    <Alert severity="warning" sx={{ mb: 1.5, py: 0.5, fontSize: '0.75rem' }}>
                                        Max follow-ups reached ({candidateFollowups}/{candidateMaxFollowups}), but manual verification is locked until <strong>all required documents</strong> are submitted ({submittedCandidateDocsCount}/{totalCandidateDocs} submitted). Please upload missing documents first.
                                    </Alert>
                                ) : null}

                                {verifyAlert && (
                                    <Alert severity={verifyAlert.type} sx={{ mb: 1.5, py: 0.5, fontSize: '0.8rem' }} onClose={() => setVerifyAlert(null)}>
                                        {verifyAlert.message}
                                    </Alert>
                                )}

                                <List disablePadding sx={{ flex: 1 }}>
                                    {candidateDocs.map((doc, idx) => {
                                        const isDocVerified = Boolean(doc.is_verified || doc.status === 'VERIFIED');
                                        const isDocSubmitted = doc.status === 'SUBMITTED';
                                        const isSelected = activeDocPreview?.doc_type === doc.doc_type;
                                        const canManuallyVerify = isManualVerificationEligible && isDocSubmitted && !isDocVerified && Boolean(doc.document_id);
                                        const isCheckedToVerify = Boolean(selectedDocsToVerify[doc.document_id]);

                                        return (
                                            <Paper
                                                key={idx}
                                                elevation={isSelected ? 2 : 0}
                                                onClick={() => doc.doc_path && setActiveDocPreview(doc)}
                                                sx={{
                                                    p: 1.5,
                                                    mb: 1.5,
                                                    borderRadius: 2,
                                                    border: isSelected ? '2px solid #1976d2' : '1px solid #e2e8f0',
                                                    cursor: doc.doc_path ? 'pointer' : 'default',
                                                    bgcolor: isSelected ? '#eff6ff' : (isDocVerified ? '#f0fdf4' : (isDocSubmitted ? '#fff' : '#fafafa')),
                                                    transition: 'all 0.15s ease-in-out',
                                                    '&:hover': doc.doc_path ? {
                                                        borderColor: '#1976d2',
                                                        boxShadow: '0 2px 8px rgba(0,0,0,0.06)'
                                                    } : {}
                                                }}
                                            >
                                                <Box display="flex" justifyContent="space-between" alignItems="flex-start">
                                                     <Box display="flex" alignItems="center" gap={1}>
                                                         {isDocVerified ? (
                                                             <Tooltip title="Document is verified">
                                                                 <CheckCircleIcon color="success" sx={{ fontSize: 18 }} />
                                                             </Tooltip>
                                                         ) : canManuallyVerify && doc.document_id ? (
                                                             <Tooltip title="Check to manually verify & approve this document">
                                                                 <Checkbox
                                                                     size="small"
                                                                     checked={isCheckedToVerify}
                                                                     onChange={(e) => {
                                                                         e.stopPropagation();
                                                                         handleToggleDocToVerify(doc);
                                                                     }}
                                                                     sx={{ p: 0 }}
                                                                 />
                                                             </Tooltip>
                                                         ) : null}
                                                         <Box>
                                                             <Typography variant="body2" fontWeight="700" color={isSelected ? 'primary.main' : 'textPrimary'}>
                                                                 {doc.display_name || doc.doc_type}
                                                                 {doc.is_mandatory && (
                                                                     <Chip label="Mandatory" size="small" color="error" variant="outlined" sx={{ ml: 0.5, height: 16, fontSize: '0.6rem' }} />
                                                                 )}
                                                             </Typography>
                                                             <Typography variant="caption" color="textSecondary">
                                                                 {doc.document_id ? `Doc ID: ${doc.document_id}` : `Type: ${doc.doc_type}`}
                                                             </Typography>
                                                         </Box>
                                                     </Box>
                                                     <Chip
                                                         label={isDocVerified ? 'Verified' : (doc.status || 'NOT_SUBMITTED')}
                                                         size="small"
                                                         color={isDocVerified ? 'success' : (doc.status === 'SUBMITTED' ? 'info' : 'warning')}
                                                         variant={isDocVerified ? 'filled' : 'outlined'}
                                                         sx={{ fontWeight: 'bold', fontSize: '0.7rem', height: 20 }}
                                                     />
                                                 </Box>

                                                {/* Verification Reason Input if checked */}
                                                {isCheckedToVerify && (
                                                    <Box mt={1.5} pt={1} borderTop="1px solid #e2e8f0" onClick={(e) => e.stopPropagation()}>
                                                        <TextField
                                                            size="small"
                                                            fullWidth
                                                            label="Verification Reason"
                                                            value={verifyReasons[doc.document_id] || ''}
                                                            onChange={(e) => handleVerifyReasonChange(doc.document_id, e.target.value)}
                                                            placeholder="e.g., Manually reviewed and matched identity details"
                                                            sx={{ fontSize: '0.75rem', bgcolor: '#fff' }}
                                                        />
                                                    </Box>
                                                )}

                                                {doc.doc_path ? (
                                                    <Box display="flex" justifyContent="space-between" alignItems="center" mt={1} pt={1} borderTop="1px dashed #e2e8f0">
                                                        <Typography variant="caption" color="primary" fontWeight="600" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                            <VisibilityIcon sx={{ fontSize: 13 }} /> {isSelected ? 'Previewing now' : 'Click to preview'}
                                                        </Typography>
                                                        <IconButton 
                                                            size="small" 
                                                            title="Open in new tab"
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                window.open(doc.doc_path, '_blank');
                                                            }}
                                                            sx={{ p: 0.5 }}
                                                        >
                                                            <OpenInNewIcon sx={{ fontSize: 14 }} />
                                                        </IconButton>
                                                    </Box>
                                                ) : (
                                                    <Box mt={0.5}>
                                                        <Typography variant="caption" color="textSecondary" fontStyle="italic">
                                                            Awaiting submission
                                                        </Typography>
                                                    </Box>
                                                )}
                                            </Paper>
                                        );
                                    })}
                                </List>

                                {/* Manual Verification Submit Panel if items selected */}
                                {Object.values(selectedDocsToVerify).some(Boolean) && (
                                    <Box sx={{ mt: 2, p: 1.5, bgcolor: '#ecfdf5', borderRadius: 2, border: '1px solid #a7f3d0' }}>
                                        <Box display="flex" justifyContent="space-between" alignItems="center">
                                            <Typography variant="caption" fontWeight="bold" color="success.dark">
                                                {Object.values(selectedDocsToVerify).filter(Boolean).length} document(s) ready to verify
                                            </Typography>
                                            <Button
                                                size="small"
                                                variant="contained"
                                                color="success"
                                                startIcon={verifyingSubmitting ? <CircularProgress size={14} color="inherit" /> : <CheckCircleIcon fontSize="small" />}
                                                disabled={verifyingSubmitting}
                                                onClick={handleManualVerifyDocuments}
                                                sx={{ textTransform: 'none', py: 0.25, fontSize: '0.75rem' }}
                                            >
                                                {verifyingSubmitting ? 'Verifying...' : 'Approve Selected'}
                                            </Button>
                                        </Box>
                                    </Box>
                                )}
                            </Box>

                            {/* Right Pane: In-App Document Viewer (58%) */}
                            <Box sx={{ width: '58%', height: '100%', display: 'flex', flexDirection: 'column', bgcolor: '#525659' }}>
                                {activeDocPreview && activeDocPreview.doc_path ? (
                                    <>
                                        <Box sx={{ p: 1.5, bgcolor: '#1e293b', color: '#fff', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                            <Typography variant="subtitle2" fontWeight="600" noWrap>
                                                Preview: {activeDocPreview.display_name || activeDocPreview.doc_type}
                                            </Typography>
                                            <Button
                                                size="small"
                                                variant="contained"
                                                color="primary"
                                                startIcon={<OpenInNewIcon fontSize="small" />}
                                                onClick={() => window.open(activeDocPreview.doc_path, '_blank')}
                                                sx={{ textTransform: 'none', py: 0.25, fontSize: '0.75rem' }}
                                            >
                                                Pop Out Tab
                                            </Button>
                                        </Box>

                                        {/* Extracted Fields Banner if available */}
                                        {activeDocPreview.extracted_data && typeof activeDocPreview.extracted_data === 'object' && Object.keys(activeDocPreview.extracted_data).length > 0 && (
                                            <Box sx={{ p: 1.5, bgcolor: '#ffffff', borderBottom: '1px solid #e2e8f0', maxHeight: 150, overflowY: 'auto' }}>
                                                <Typography variant="caption" fontWeight="bold" color="textSecondary" sx={{ display: 'block', mb: 0.75, letterSpacing: 0.5 }}>
                                                    DOCUMENT FIELDS & EXTRACTED DATA ({Object.keys(activeDocPreview.extracted_data).length})
                                                </Typography>
                                                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                                                    {Object.entries(activeDocPreview.extracted_data).map(([k, v]) => (
                                                        <Box key={k} sx={{ px: 1.25, py: 0.5, bgcolor: '#f8fafc', borderRadius: 1.5, border: '1px solid #e2e8f0' }}>
                                                            <Typography variant="caption" color="textSecondary" sx={{ textTransform: 'capitalize', fontWeight: 600 }}>
                                                                {k.replace(/_/g, ' ')}:
                                                            </Typography>
                                                            <Typography variant="caption" fontWeight="700" color="#0f172a" sx={{ ml: 0.5 }}>
                                                                {String(v || '—')}
                                                            </Typography>
                                                        </Box>
                                                    ))}
                                                </Box>
                                            </Box>
                                        )}

                                        <Box sx={{ flex: 1, width: '100%', height: '100%', bgcolor: '#f1f5f9' }}>
                                            <iframe
                                                src={activeDocPreview.doc_path}
                                                title={activeDocPreview.display_name || 'Document Preview'}
                                                width="100%"
                                                height="100%"
                                                style={{ border: 'none', display: 'block' }}
                                            />
                                        </Box>
                                    </>
                                ) : (
                                    <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', p: 4, bgcolor: '#f8fafc', color: 'text.secondary' }}>
                                        <DescriptionIcon sx={{ fontSize: 60, color: '#cbd5e1', mb: 2 }} />
                                        <Typography variant="h6" fontWeight="600" color="textSecondary">
                                            No Document Selected
                                        </Typography>
                                        <Typography variant="body2" color="textSecondary" align="center" sx={{ maxWidth: 320, mt: 0.5 }}>
                                            Select any submitted document from the left checklist to preview it right here inside this window.
                                        </Typography>
                                    </Box>
                                )}
                            </Box>
                        </>
                    )}
                </DialogContent>
                <DialogActions sx={{ p: 2 }}>
                    <Button onClick={handleCloseDocsModal} variant="contained">Done</Button>
                </DialogActions>
            </Dialog>

            {/* Manual Document Upload Dialog */}
            <Dialog
                open={uploadModalOpen}
                onClose={uploadSubmitting ? undefined : handleCloseUploadModal}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle>
                    <Typography variant="h6" fontWeight="bold">Manual Document Upload</Typography>
                    <Typography variant="body2" color="textSecondary">
                        Candidate has reached 3+ follow-ups. Select missing documents to upload on their behalf.
                    </Typography>
                </DialogTitle>
                <DialogContent dividers>
                    {selectedCandidateForUpload && (
                        <Box>
                            <Box sx={{ mb: 2, p: 2, bgcolor: '#f8fafc', borderRadius: 2 }}>
                                <Typography variant="subtitle2" fontWeight="bold">{selectedCandidateForUpload.candidate_name}</Typography>
                                <Typography variant="body2" color="textSecondary">
                                    Pre-Onboarding ID: {selectedCandidateForUpload.pre_onboarding_id || 'N/A'} | Follow-ups: {selectedCandidateForUpload.followup_count || 0}
                                </Typography>
                            </Box>

                            {/* Show Result Alerts if response returned */}
                            {uploadResults && (
                                <Box sx={{ mb: 2 }}>
                                    {uploadResults.successful && uploadResults.successful.length > 0 && (
                                        <Alert severity="success" sx={{ mb: 1 }}>
                                            <strong>Successfully uploaded {uploadResults.successful.length} document(s):</strong>
                                            <ul style={{ margin: '4px 0 0', paddingLeft: '20px' }}>
                                                {uploadResults.successful.map((s, idx) => (
                                                    <li key={idx}>{s.document_type || s.doc_type} ({s.file_name})</li>
                                                ))}
                                            </ul>
                                        </Alert>
                                    )}
                                    {uploadResults.failed && uploadResults.failed.length > 0 && (
                                        <Alert severity="error">
                                            <strong>Failed {uploadResults.failed.length} document(s):</strong>
                                            <ul style={{ margin: '4px 0 0', paddingLeft: '20px' }}>
                                                {uploadResults.failed.map((f, idx) => (
                                                    <li key={idx}>{f.doc_type}: {f.reason || 'Failed to upload'}</li>
                                                ))}
                                            </ul>
                                        </Alert>
                                    )}
                                </Box>
                            )}

                            <Typography variant="subtitle2" fontWeight="600" mb={1.5}>Select Documents to Upload</Typography>

                            {/* Only offer NOT_SUBMITTED documents for manual upload */}
                            {(() => {
                                const unsubmittedDocs = (selectedCandidateForUpload.required_documents || []).filter(
                                    d => d.status === 'NOT_SUBMITTED' || !d.status
                                );

                                if (unsubmittedDocs.length === 0) {
                                    return (
                                        <Alert severity="info">All required documents have already been submitted.</Alert>
                                    );
                                }

                                return unsubmittedDocs.map((doc) => {
                                    const selectedFile = selectedFilesToUpload[doc.doc_type];
                                    return (
                                        <Box 
                                            key={doc.doc_type} 
                                            sx={{ 
                                                p: 2, 
                                                border: '1px solid #e2e8f0', 
                                                borderRadius: 2, 
                                                mb: 1.5,
                                                bgcolor: selectedFile ? '#f0f9ff' : '#fff'
                                            }}
                                        >
                                            <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                                                <Typography variant="body2" fontWeight="600">
                                                    {doc.display_name || doc.doc_type}
                                                    {doc.is_mandatory && (
                                                        <Chip label="Mandatory" size="small" color="error" variant="outlined" sx={{ ml: 1, height: 18, fontSize: '0.65rem' }} />
                                                    )}
                                                </Typography>
                                                <Typography variant="caption" color="textSecondary">{doc.doc_type}</Typography>
                                            </Box>
                                            <Box display="flex" alignItems="center" gap={2}>
                                                <Button
                                                    variant="outlined"
                                                    component="label"
                                                    size="small"
                                                    startIcon={<UploadFileIcon />}
                                                    sx={{ textTransform: 'none' }}
                                                >
                                                    {selectedFile ? 'Change File' : 'Choose File'}
                                                    <input
                                                        type="file"
                                                        hidden
                                                        accept=".pdf,.png,.jpg,.jpeg"
                                                        onChange={(e) => {
                                                            if (e.target.files && e.target.files[0]) {
                                                                handleFileSelectForDoc(doc.doc_type, e.target.files[0]);
                                                            }
                                                        }}
                                                    />
                                                </Button>
                                                <Typography variant="body2" color={selectedFile ? "primary" : "textSecondary"} noWrap sx={{ maxWidth: '280px' }}>
                                                    {selectedFile ? selectedFile.name : 'No file selected'}
                                                </Typography>
                                            </Box>
                                        </Box>
                                    );
                                });
                            })()}
                        </Box>
                    )}
                </DialogContent>
                <DialogActions sx={{ p: 2 }}>
                    <Button onClick={handleCloseUploadModal} disabled={uploadSubmitting}>
                        {uploadResults ? 'Close' : 'Cancel'}
                    </Button>
                    <Button
                        variant="contained"
                        color="primary"
                        startIcon={uploadSubmitting ? <CircularProgress size={18} color="inherit" /> : <CloudUploadIcon />}
                        onClick={handleUploadDocuments}
                        disabled={uploadSubmitting || Object.values(selectedFilesToUpload).filter(Boolean).length === 0}
                    >
                        {uploadSubmitting ? 'Uploading...' : 'Submit Documents'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Production Candidate Document Drawer */}
            <CandidateDocumentDrawer
                open={drawerOpen}
                onClose={() => setDrawerOpen(false)}
                candidate={selectedCandidateForDrawer}
                onUpdated={() => fetchPreOnboardingCandidates(preOnboardingPage)}
            />
        </Box>
    );
};

export default OfferManagement;
